// ArchIE headless run state machine (fn-69.3, R2/R4/R5).
//
// A PURE reducer + helpers that turn the headless driver's view events
// (fn-69.2 `classifyEvent`) and approval decisions into the UI's render
// state. The side-effectful polling/approval orchestration lives in
// `use-headless-run.ts`; everything here is deterministic and unit-
// tested, so the page renders a tested projection rather than ad-hoc
// `useState` soup.
//
// Design notes:
//   - `status` is the CURRENT shimmer line (last good), `history` the
//     completed scrollback. A `fallback` view event NEVER overwrites a
//     good line with raw JSON — it shows the NEUTRAL status (the driver
//     already mapped garbage → `NEUTRAL_FALLBACK_STATUS`).
//   - A terminal phase (`done` / `error` / `blocked`) flips `phase` to
//     `done`/`error` and freezes the result. The orchestrator stops
//     polling once the machine is terminal.

import type { AckKind, HeadlessRunEvent, HeadlessStatus } from '../../headless/index.js';
import { NEUTRAL_FALLBACK_STATUS, bareToolName } from '../../headless/index.js';

/** Driver-side approval mode (mirrors `@headless` `ApprovalMode`). */
export type RunMode = 'lights-out' | 'attended';

/** High-level run lifecycle the page renders off of. */
export type RunPhase =
  | 'idle' // intake form, nothing started
  | 'starting' // creating session + sending kickoff
  | 'running' // streaming status
  | 'awaiting-gate' // attended: a restricted-form card is shown, waiting on the human
  | 'done' // terminal success
  | 'error'; // terminal failure / backend-unavailable

/** A pending restricted-form gate surfaced to the human (attended mode). */
export interface GateView {
  readonly approvalId: string;
  readonly sessionId: string;
  /** Raw (possibly federated) tool name, e.g. `smokeball___create_contact`. */
  readonly toolName: string;
  /** Bare name after the last `___`, e.g. `create_contact`. */
  readonly bareName: string;
  readonly ackKind: AckKind;
  readonly toolArgs: Record<string, unknown>;
}

/** One tool-activity line (read/dedupe/write), shown subtly under the shimmer. */
export interface ToolActivity {
  readonly tool: string;
  readonly kind: 'builtin' | 'mcp' | 'custom';
}

export type RunResult =
  | { readonly kind: 'created'; readonly matter?: Record<string, unknown> }
  | { readonly kind: 'rejected'; readonly tool: string }
  | { readonly kind: 'unavailable'; readonly reason: string; readonly lastPhase: string };

export interface RunState {
  readonly phase: RunPhase;
  readonly mode: RunMode;
  readonly sessionId?: string;
  /** The current shimmer status line (last good, or neutral fallback). */
  readonly status: HeadlessStatus;
  /**
   * Whether the MOST RECENT agent turn was non-conforming (a parse
   * fallback). The displayed `status` still holds the last GOOD line (R2
   * "keep the last good line") — this flag only drives a subtle marker;
   * it never means raw JSON is on screen.
   */
  readonly statusIsFallback: boolean;
  /**
   * The last successfully-parsed status (drives `status` + what gets
   * archived). `undefined` until the first good line — before that a
   * fallback shows the NEUTRAL status, never the seed.
   */
  readonly lastGood?: HeadlessStatus;
  /** Completed status lines, oldest first (scrollback). */
  readonly history: readonly HeadlessStatus[];
  /** Recent tool activity (most recent last). */
  readonly tools: readonly ToolActivity[];
  /** The restricted-form card awaiting the human, when `phase === 'awaiting-gate'`. */
  readonly pendingGate?: GateView;
  /** R5 proof: the CP-re-stamped `mounted_files[].real_path`. */
  readonly mountedRealPaths: readonly string[];
  /** Terminal result, when `phase` is `done` / `error`. */
  readonly result?: RunResult;
}

const STARTING_STATUS: HeadlessStatus = { phase: 'starting', title: 'Starting intake…' };

export function initialRunState(mode: RunMode): RunState {
  return {
    phase: 'idle',
    mode,
    status: STARTING_STATUS,
    statusIsFallback: false,
    history: [],
    tools: [],
    mountedRealPaths: [],
  };
}

/** Phases that END the run (the agent's own terminal signal). */
const TERMINAL_PHASES: ReadonlySet<string> = new Set(['done', 'error', 'blocked']);

export function isTerminalPhase(phase: string): boolean {
  return TERMINAL_PHASES.has(phase.trim().toLowerCase());
}

export type RunAction =
  | { readonly type: 'RESET' }
  | { readonly type: 'START'; readonly mode: RunMode }
  | {
      readonly type: 'SESSION_CREATED';
      readonly sessionId: string;
      readonly mountedRealPaths: readonly string[];
    }
  | { readonly type: 'STATUS'; readonly status: HeadlessStatus }
  | { readonly type: 'FALLBACK' }
  | { readonly type: 'TOOL'; readonly tool: string; readonly kind: ToolActivity['kind'] }
  | { readonly type: 'GATE_PENDING'; readonly gate: GateView }
  | { readonly type: 'GATE_CLEARED' }
  | { readonly type: 'DONE'; readonly result: RunResult }
  | { readonly type: 'ERROR'; readonly reason: string };

/** Cap the rendered tool/history backlog so a long run can't grow unbounded. */
const MAX_HISTORY = 50;
const MAX_TOOLS = 50;

function pushCapped<T>(list: readonly T[], item: T, cap: number): T[] {
  const next = [...list, item];
  return next.length > cap ? next.slice(next.length - cap) : next;
}

export function runReducer(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case 'RESET':
      return initialRunState(state.mode);

    case 'START':
      return { ...initialRunState(action.mode), phase: 'starting', status: STARTING_STATUS };

    case 'SESSION_CREATED':
      return {
        ...state,
        phase: state.phase === 'starting' ? 'running' : state.phase,
        sessionId: action.sessionId,
        mountedRealPaths: action.mountedRealPaths,
      };

    case 'STATUS': {
      // Archive the PRIOR good line (only good lines ever land in history —
      // the seed + neutral fallbacks never do), then advance.
      const history =
        state.lastGood !== undefined
          ? pushCapped(state.history, state.lastGood, MAX_HISTORY)
          : state.history;
      return {
        ...state,
        phase: state.phase === 'starting' ? 'running' : state.phase,
        status: action.status,
        lastGood: action.status,
        statusIsFallback: false,
        history,
      };
    }

    case 'FALLBACK':
      // R2: keep the last GOOD line on screen (never raw JSON); only when
      // no good line has arrived yet do we show the NEUTRAL status. Mark
      // the turn as a fallback for a subtle UI signal. History untouched —
      // a non-conforming turn is not an archived phase.
      return {
        ...state,
        phase: state.phase === 'starting' ? 'running' : state.phase,
        status: state.lastGood ?? NEUTRAL_FALLBACK_STATUS,
        statusIsFallback: true,
      };

    case 'TOOL':
      return {
        ...state,
        tools: pushCapped(state.tools, { tool: action.tool, kind: action.kind }, MAX_TOOLS),
      };

    case 'GATE_PENDING':
      return { ...state, phase: 'awaiting-gate', pendingGate: action.gate };

    case 'GATE_CLEARED': {
      const { pendingGate: _cleared, ...rest } = state;
      return { ...rest, phase: state.phase === 'awaiting-gate' ? 'running' : state.phase };
    }

    case 'DONE': {
      const { pendingGate: _g, ...rest } = state;
      return { ...rest, phase: 'done', result: action.result };
    }

    case 'ERROR': {
      const { pendingGate: _g, ...rest } = state;
      return {
        ...rest,
        phase: 'error',
        result: { kind: 'unavailable', reason: action.reason, lastPhase: state.status.phase },
      };
    }

    default:
      return state;
  }
}

// ── Pure projections ─────────────────────────────────────────────────

/**
 * Extract the CP-re-stamped `mounted_files[].real_path` from a session
 * row's metadata (R5 proof). Defensive against any shape — returns the
 * non-empty string `real_path`s, else `[]`.
 */
export function extractMountedRealPaths(metadata: unknown): string[] {
  if (metadata === null || typeof metadata !== 'object') {
    return [];
  }
  const mounted = (metadata as { mounted_files?: unknown }).mounted_files;
  if (!Array.isArray(mounted)) {
    return [];
  }
  const out: string[] = [];
  for (const entry of mounted) {
    if (entry !== null && typeof entry === 'object') {
      const realPath = (entry as { real_path?: unknown }).real_path;
      if (typeof realPath === 'string' && realPath.length > 0) {
        out.push(realPath);
      }
    }
  }
  return out;
}

/** Build the human-facing gate card model from a resolved approval row. */
export function toGateView(row: {
  readonly id: string;
  readonly session_id: string;
  readonly tool_name: string;
  readonly ack_kind: AckKind;
  readonly tool_args: Record<string, unknown>;
}): GateView {
  return {
    approvalId: row.id,
    sessionId: row.session_id,
    toolName: row.tool_name,
    bareName: bareToolName(row.tool_name),
    ackKind: row.ack_kind,
    toolArgs: row.tool_args,
  };
}

/** Known + best-effort fields for the `review_matter` draft card. */
export interface MatterDraftFields {
  readonly name?: string;
  readonly matterType?: string;
  readonly description?: string;
  readonly client?: string;
  readonly otherSide?: string;
  /** The matter id when the draft/result carries one (final-result render). */
  readonly id?: string;
  /** Remaining top-level scalar fields, for a generic fallback render. */
  readonly extra: ReadonlyArray<readonly [string, string]>;
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return undefined;
}

function firstString(args: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const v = asString(args[key]);
    if (v !== undefined) {
      return v;
    }
  }
  return undefined;
}

const KNOWN_MATTER_KEYS = new Set([
  'id',
  'matter_id',
  'matterId',
  'name',
  'matter_name',
  'title',
  'matter_type',
  'matterType',
  'type',
  'description',
  'summary',
  'client',
  'client_name',
  'other_side',
  'otherSide',
  'other_party',
  // Nested containers consumed below — never dump them raw into `extra`.
  'parties',
]);

/** Pull a display name from a party-like object, trying common key shapes. */
function partyName(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return asString(value);
  }
  if (value === null || typeof value !== 'object') {
    return undefined;
  }
  return firstString(value as Record<string, unknown>, [
    'name',
    'display_name',
    'displayName',
    'full_name',
    'fullName',
    'label',
  ]);
}

/** True when a party's role/side string denotes the client side. */
function isClientRole(role: string | undefined): boolean {
  return role !== undefined && /client/i.test(role);
}

/**
 * Resolve client vs other-side names from a nested `parties` structure —
 * the real `review_matter` draft carries parties as an array (role-keyed)
 * or an object, not flat `client`/`other_side` scalars (live shape per
 * memory `project_matter_creator_e2e_findings`).
 */
function derivePartiesFields(args: Record<string, unknown>): {
  client?: string;
  otherSide?: string;
} {
  const parties = args.parties;
  const out: { client?: string; otherSide?: string } = {};

  if (Array.isArray(parties)) {
    for (const entry of parties) {
      if (entry === null || typeof entry !== 'object') {
        continue;
      }
      const e = entry as Record<string, unknown>;
      const role = firstString(e, ['role', 'side', 'party_role', 'type', 'relationship']);
      const kind = firstString(e, ['kind']);
      const nm = partyName(e);
      if (nm === undefined) {
        continue;
      }
      // The live `review_matter` party shape marks the client side via
      // `kind: 'client'` and/or `actingFor: true`, not always a role-like
      // key (memory `project_matter_creator_e2e_findings`). Treat any of
      // those as client-side, else it falls to the other side.
      const clientSide = isClientRole(role) || isClientRole(kind) || e.actingFor === true;
      if (clientSide && out.client === undefined) {
        out.client = nm;
      } else if (!clientSide && out.otherSide === undefined) {
        out.otherSide = nm;
      }
    }
  } else if (parties !== null && typeof parties === 'object') {
    const p = parties as Record<string, unknown>;
    const client = partyName(p.client ?? p.client_name);
    const otherSide = partyName(p.other_side ?? p.otherSide ?? p.other_party);
    if (client !== undefined) {
      out.client = client;
    }
    if (otherSide !== undefined) {
      out.otherSide = otherSide;
    }
  }

  return out;
}

/**
 * Project a `review_matter` `tool_args` draft (or a created-matter result)
 * into legible fields. Top-level scalars map to labelled rows; the nested
 * `parties` container + an object `matterType` are unwrapped; any other
 * top-level scalar lands in `extra`. Everything renders as TEXT by the
 * card (never HTML/JSON).
 */
export function deriveMatterFields(args: Record<string, unknown>): MatterDraftFields {
  const id = firstString(args, ['id', 'matter_id', 'matterId']);
  const name = firstString(args, ['name', 'matter_name', 'title']);
  // `matter_type` may be a scalar OR a nested object ({name|label|...}).
  const matterType =
    firstString(args, ['matter_type', 'matterType', 'type']) ??
    partyName(args.matter_type ?? args.matterType ?? args.type);
  const description = firstString(args, ['description', 'summary']);

  const parties = derivePartiesFields(args);
  const client = firstString(args, ['client', 'client_name']) ?? parties.client;
  const otherSide =
    firstString(args, ['other_side', 'otherSide', 'other_party']) ?? parties.otherSide;

  const extra: Array<readonly [string, string]> = [];
  for (const [key, value] of Object.entries(args)) {
    if (KNOWN_MATTER_KEYS.has(key)) {
      continue;
    }
    const scalar = asString(value);
    if (scalar !== undefined) {
      extra.push([key, scalar]);
    }
  }

  return {
    ...(id !== undefined ? { id } : {}),
    ...(name !== undefined ? { name } : {}),
    ...(matterType !== undefined ? { matterType } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(client !== undefined ? { client } : {}),
    ...(otherSide !== undefined ? { otherSide } : {}),
    extra,
  };
}

/** Flatten a tool-result `content` (string | text blocks) into one string. */
export function toolResultText(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }
  if (!Array.isArray(content)) {
    return '';
  }
  const parts: string[] = [];
  for (const block of content) {
    if (typeof block === 'string') {
      parts.push(block);
    } else if (block !== null && typeof block === 'object') {
      const text = (block as { text?: unknown }).text;
      if (typeof text === 'string') {
        parts.push(text);
      }
    }
  }
  return parts.join('');
}

function unwrapMatterEnvelope(value: unknown): Record<string, unknown> | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const obj = value as Record<string, unknown>;
  // A create_* tool commonly returns `{ matter: {...} }` / `{ data: {...} }`.
  for (const key of ['matter', 'data', 'result']) {
    const nested = obj[key];
    if (nested !== null && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>;
    }
  }
  return obj;
}

/**
 * Best-effort parse of a `create_matter` tool RESULT into the created
 * matter object (R5: the result must reflect the actual create, not the
 * review draft). JSON-parses the result text, unwraps a `matter`/`data`
 * envelope, and accepts it only when it carries an id OR recognizable
 * matter fields. Returns `undefined` on any miss — never throws.
 */
export function readToolResultMatter(content: unknown): Record<string, unknown> | undefined {
  const text = toolResultText(content).trim();
  if (text.length === 0) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return undefined;
  }
  const obj = unwrapMatterEnvelope(parsed);
  if (obj === undefined) {
    return undefined;
  }
  const hasId = ['id', 'matter_id', 'matterId'].some((k) => {
    const v = obj[k];
    return typeof v === 'string' || typeof v === 'number';
  });
  const fields = deriveMatterFields(obj);
  if (hasId || fields.name !== undefined || fields.matterType !== undefined) {
    return obj;
  }
  return undefined;
}

// ── create_matter observation (R5) ───────────────────────────────────

/**
 * The minimal mutable surface `observeCreateMatter` reads + writes. The
 * run loop's `LoopCtx` satisfies this structurally; tests pass a plain
 * object.
 */
export interface CreateMatterObserver {
  /** Correlator of the in-flight `create_matter` call, to match its result. */
  createMatterToolUseId: string | undefined;
  /**
   * The created matter — set from a NON-ERROR `create_matter` tool RESULT,
   * displayed using the confirmed review draft + any id the result carries.
   * `undefined` means no create result was observed → a graceful unavailable
   * terminal state.
   */
  createdMatter: Record<string, unknown> | undefined;
  /**
   * The matter the operator confirmed at the `review_matter` gate (the rich
   * source for the result panel — name/type/parties). Overlaid by the
   * backend id from the create result. Optional: a bare observer (tests)
   * may omit it.
   */
  reviewedMatter?: Record<string, unknown> | undefined;
}

const TOOL_USE_EVENTS: ReadonlySet<string> = new Set([
  'agent.mcp_tool_use',
  'agent.tool_use',
  'agent.custom_tool_use',
]);
const TOOL_RESULT_EVENTS: ReadonlySet<string> = new Set([
  'agent.mcp_tool_result',
  'agent.tool_result',
  'agent.custom_tool_result',
]);

function eventStr(data: Record<string, unknown>, key: string): string | undefined {
  const v = data[key];
  return typeof v === 'string' ? v : undefined;
}

/**
 * The correlator a tool-USE event exposes. MCP `agent.mcp_tool_use` (and
 * built-in `agent.tool_use`) events can OMIT `tool_use_id` on
 * Anthropic-direct; the platform's own stream reducers fall back to the
 * event id (`tool_use_id ?? ev.id`, see `reduce-stream.ts`) and a
 * tool_result then references THAT id. We mirror that fallback EXACTLY so
 * a successful create with no `tool_use_id` is still matched to its
 * result. `agent.custom_tool_use` carries `custom_tool_use_id` instead.
 */
function toolUseCorrelator(ev: HeadlessRunEvent): string {
  return eventStr(ev.data, 'tool_use_id') ?? eventStr(ev.data, 'custom_tool_use_id') ?? ev.id;
}

/**
 * The correlator a tool-RESULT event references, across wire variance:
 * MCP results carry `mcp_tool_use_id` (falling back to `tool_use_id`),
 * built-in results `tool_use_id`, custom results `custom_tool_use_id`.
 * The matched value equals the originating use event's `toolUseCorrelator`
 * (incl. the `ev.id` fallback). `undefined` when none is present.
 */
function toolResultCorrelator(ev: HeadlessRunEvent): string | undefined {
  return (
    eventStr(ev.data, 'mcp_tool_use_id') ??
    eventStr(ev.data, 'tool_use_id') ??
    eventStr(ev.data, 'custom_tool_use_id')
  );
}

/**
 * Track the `create_matter` call + its result on the raw event stream so
 * the final result reflects the ACTUAL create (incl. the backend-assigned
 * id), not the `review_matter` draft. Mutates `ctx.createMatterToolUseId`
 * / `ctx.createdMatter` in place.
 *
 * Correlation mirrors the stream reducer's `tool_use_id ?? ev.id` rule, so
 * a `create_matter` use event that omits `tool_use_id` (Anthropic-direct)
 * is still matched to its result by the event id — otherwise a SUCCESSFUL
 * create would be missed and the UI would wrongly render the graceful
 * "unavailable" terminal state.
 */
export function observeCreateMatter(ctx: CreateMatterObserver, ev: HeadlessRunEvent): void {
  if (TOOL_USE_EVENTS.has(ev.type)) {
    const name = eventStr(ev.data, 'name');
    if (name !== undefined && bareToolName(name) === 'create_matter') {
      ctx.createMatterToolUseId = toolUseCorrelator(ev);
    }
    return;
  }
  if (
    TOOL_RESULT_EVENTS.has(ev.type) &&
    ctx.createMatterToolUseId !== undefined &&
    toolResultCorrelator(ev) === ctx.createMatterToolUseId
  ) {
    // An errored tool result is NOT a successful create — leave
    // `createdMatter` unset so the run ends gracefully unavailable.
    if ((ev.data as { is_error?: unknown }).is_error === true) {
      return;
    }
    // A NON-ERROR create_matter result means the write SUCCEEDED. Mark it
    // created even when the result body has no parseable matter object — the
    // agent often surfaces the backend id only in its final message, and a
    // thin/confirmation-only result must NOT read as "backend unavailable".
    // Base the display on the confirmed review draft (name/type/parties) and
    // overlay any id/fields the result itself carries.
    const matter = readToolResultMatter((ev.data as { content?: unknown }).content);
    ctx.createdMatter = {
      ...(ctx.reviewedMatter ?? {}),
      ...(ctx.createdMatter ?? {}),
      ...(matter ?? {}),
    };
  }
}
